import React from 'react';
import { Modal, Tag } from 'antd';
import {
  UserOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  LaptopOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import moment from 'moment';

const ActivityDetailModal = ({ visible, onClose, activity }) => {
  if (!activity) return null;

  const formatJson = (str) => {
    if (!str) return '—';
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  const hasDiff = activity.beforeValue || activity.afterValue;

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThunderboltOutlined style={{ color: '#ff6700' }} />
          Activity Detail
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={680}
      className="activity-detail-modal"
      centered
    >
      <div className="activity-detail-grid">
        {/* Row 1 */}
        <div className="activity-detail-item">
          <span className="activity-detail-label">
            <UserOutlined style={{ marginRight: 4 }} /> User ID
          </span>
          <span className="activity-detail-value">{activity.userId || '—'}</span>
        </div>
        <div className="activity-detail-item">
          <span className="activity-detail-label">
            <UserOutlined style={{ marginRight: 4 }} /> User Name
          </span>
          <span className="activity-detail-value">{activity.userName || '—'}</span>
        </div>

        {/* Row 2 */}
        <div className="activity-detail-item">
          <span className="activity-detail-label">User Type</span>
          <span className="activity-detail-value">
            <Tag
              color={activity.userType === 'ADMIN' ? 'volcano' : 'geekblue'}
              style={{ fontWeight: 600, borderRadius: 6 }}
            >
              {activity.userType}
            </Tag>
          </span>
        </div>
        <div className="activity-detail-item">
          <span className="activity-detail-label">
            <AppstoreOutlined style={{ marginRight: 4 }} /> Module
          </span>
          <span className="activity-detail-value">{activity.module || '—'}</span>
        </div>

        {/* Row 3 */}
        <div className="activity-detail-item">
          <span className="activity-detail-label">Action Performed</span>
          <span className="activity-detail-value" style={{ fontWeight: 600 }}>
            {activity.action || '—'}
          </span>
        </div>
        <div className="activity-detail-item">
          <span className="activity-detail-label">
            <ClockCircleOutlined style={{ marginRight: 4 }} /> Timestamp
          </span>
          <span className="activity-detail-value">
            {(() => {
              const ts = activity.timestamp || activity.createdAt || activity.eventTime || activity.time || activity.date;
              return ts ? moment(ts).format('MMM DD, YYYY  •  hh:mm:ss A') : '—';
            })()}
          </span>
        </div>

        {/* Description spans full width when present */}
        <div className="activity-detail-item" style={{ gridColumn: '1 / -1' }}>
          <span className="activity-detail-label">Description</span>
          <span className="activity-detail-value">{activity.description || '—'}</span>
        </div>

        {/* Row 4 */}
        <div className="activity-detail-item">
          <span className="activity-detail-label">
            <GlobalOutlined style={{ marginRight: 4 }} /> IP Address
          </span>
          <span
            className="activity-detail-value"
            style={{ fontFamily: 'monospace' }}
          >
            {activity.ipAddress || '—'}
          </span>
        </div>
        <div className="activity-detail-item">
          <span className="activity-detail-label">
            <LaptopOutlined style={{ marginRight: 4 }} /> Device
          </span>
          <span className="activity-detail-value">{activity.deviceType || '—'}</span>
        </div>

        {activity.role && (
          <div className="activity-detail-item">
            <span className="activity-detail-label">Role</span>
            <span className="activity-detail-value">{activity.role}</span>
          </div>
        )}

        {activity.accountNumber && (
          <div className="activity-detail-item">
            <span className="activity-detail-label">Account Number</span>
            <span
              className="activity-detail-value"
              style={{ fontFamily: 'monospace', fontWeight: 600 }}
            >
              {activity.accountNumber}
            </span>
          </div>
        )}

        {activity.channel && (
          <div className="activity-detail-item">
            <span className="activity-detail-label">Channel</span>
            <span className="activity-detail-value">
              <Tag
                color={
                  activity.channel === 'Web'
                    ? 'green'
                    : activity.channel === 'Mobile'
                    ? 'purple'
                    : 'default'
                }
                style={{ fontWeight: 600, borderRadius: 6 }}
              >
                {activity.channel}
              </Tag>
            </span>
          </div>
        )}

        {activity.loginStatus && (
          <div className="activity-detail-item">
            <span className="activity-detail-label">Login Status</span>
            <span className="activity-detail-value">
              <Tag
                color={activity.loginStatus === 'SUCCESS' ? 'success' : 'error'}
                style={{ fontWeight: 600, borderRadius: 6 }}
              >
                {activity.loginStatus}
              </Tag>
            </span>
          </div>
        )}

        {/* Diff Section */}
        {hasDiff && (
          <>
            <hr className="activity-detail-divider" />
            <div className="activity-diff-container">
              <div className="activity-diff-box diff-before">
                <div className="activity-diff-title diff-before">Before Value</div>
                <pre className="activity-diff-content">
                  {formatJson(activity.beforeValue) || '—'}
                </pre>
              </div>
              <div className="activity-diff-box diff-after">
                <div className="activity-diff-title diff-after">After Value</div>
                <pre className="activity-diff-content">
                  {formatJson(activity.afterValue) || '—'}
                </pre>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ActivityDetailModal;
